/**
 * Helpers alineados con @nebula-layout/layout (bundle interno).
 * El paquete publica estos símbolos en .d.ts pero no los reexporta en el .mjs,
 * por eso vivimos aquí una copia mínima + soporte explícito para Nebula Vault.
 */

export type NebulaModuleFromRoute =
  | 'accounting'
  | 'tesorería'
  | 'budget'
  | 'vault'
  | 'products'
  | 'company'
  | null;

/** Lista de rutas “relativas” válidas del ERP (misma que el bundle de layout). */
const VALID_ROUTES = [
  'bank-account/list',
  'embargo/list',
  'income-voucher/list',
  'payment-order-reception/list',
  'expense-voucher/list',
  'bank-payment/list',
  'payment-annulment/list',
  'payment-confirmation/list',
  'electronic-payment-config/list',
  'electronic-payment-gen/list',
  'bank-transfer/list',
  'bank-transfer-annulment/list',
  'bank-closure/list',
  'masters/cost-center/list',
  'masters/cost-center-type/list',
  'masters/masters',
  'department-city/list',
  'accrual/list',
  'accrual-approval/list',
  'accrual-annulment/list',
  'journal-entry/list',
  'journal-entry-annulment/list',
  'depreciation/list',
  'deferred-process/list',
  'accounting-closure/list',
  'bank-reconciliation-config/list',
  'bank-reconciliation/list',
  'billing-parameters/list',
  'billing-concept/list',
  'invoice-elaboration/list',
  'credit-debit-note/list',
  'budget-parameters/list',
  'resource-type/list',
  'funding-source/list',
];

/** Rutas relativas válidas dentro de /layout/vault (lista blanca opcional). */
const VAULT_ROUTES = [
  'dashboard',
  'preservation',
  'vault',
  'vault/projects',
  'vault/upload',
  'vault/files',
  'vault/files/:projectId',
  'vault/projects/:id/upload',
  'vault/projects/:id',
  'retention',
  'legal-hold',
  'reports',
  'cloud',
  'notifications',
  'audit',
  'settings',
  'settings/profile',
  'admin',
  'admin/dashboard',
  'admin/tenants',
  'admin/users',
  'admin/user',
  'admin/servers',
  'admin/module-permissions',
  'preservation-messages',
  'repository-admin',
];

/** Verifica si una ruta pertenece al módulo Vault. */
export function isVaultRoute(route: string): boolean {
  if (!route) return false;

  const lower = route.toLowerCase();
  if (lower.includes('/layout/vault') || lower.includes('/vault/')) {
    return true;
  }

  const cleanRoute = route
    .split('?')[0]
    .split('#')[0]
    .replace(/^\/+|\/+$/g, '')
    .toLowerCase();

  return VAULT_ROUTES.some((valid) => {
    const validPattern = valid.replace(/:\w+/g, '[^/]+');
    const regex = new RegExp(`^${validPattern}$`);
    return regex.test(cleanRoute.split('/').slice(-valid.split('/').length).join('/'));
  });
}

export function isRouteValid(route: string): boolean {
  if (!route) return false;
  const noQuery = route.split('?')[0].split('#')[0];
  if (noQuery.toLowerCase().includes('/layout/vault')) {
    return true;
  }
  const cleanRoute = noQuery
    .replace(/^\/layout\/(accounting|tesorería|tesoreria|budget|products|company)\//i, '')
    .toLowerCase()
    .trim();
  return VALID_ROUTES.some(
    (valid) =>
      cleanRoute === valid.toLowerCase() ||
      cleanRoute.endsWith('/' + valid.toLowerCase()),
  );
}

export function getModuleFromRoute(route: string): NebulaModuleFromRoute {
  const lower = route.toLowerCase();
  if (lower.includes('/layout/vault') || lower.includes('/vault/')) {
    return 'vault';
  }
  if (lower.includes('/budget/') || lower.includes('budget-')) return 'budget';
  if (
    lower.includes('/tesorería/') ||
    lower.includes('/tesoreria/') ||
    lower.includes('treasury') ||
    lower.includes('banco') ||
    lower.includes('embargo')
  ) {
    return 'tesorería';
  }
  if (lower.includes('/accounting/') || lower.includes('accounting')) return 'accounting';
  if (lower.includes('/products/') || lower.includes('products')) return 'products';
  if (lower.includes('/company/') || lower.includes('company')) return 'company';
  return null;
}

/** Construye una ruta completa para Vault (sidebar / servicios). */
export function buildVaultRoute(path: string, params?: Record<string, string | number>): string {
  const basePath = '/layout/vault';
  let route = `${basePath}/${path}`.replace(/\/+/g, '/');

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      route = route.replace(`:${key}`, String(value));
    });
  }

  return route;
}
